import React, { useEffect, useMemo, useRef, useState } from "react";

import PlaybackSearchPanel from "../components/playback/PlaybackSearchPanel";
import PlaybackCameraList from "../components/playback/PlaybackCameraList";
import PlaybackVideoPanel from "../components/playback/PlaybackVideoPanel";
import PlaybackTimeline from "../components/playback/PlaybackTimeline";

import {
  PLAYBACK_API,
  buildAxisLabels,
  buildTimelineRuns,
  clampWindowStart,
  findClosestSegmentBySec,
  findSegmentContainingSec,
  getSegStartSec,
  getSegEndSec,
  getTodayDateString,
  getZoomSeconds,
  normalizePlaybackCamera,
  normalizePlaybackSegment,
  parseTimeToSec,
  toYearMonth,
} from "../utils/playbackUtils";

export default function PlaybackPage() {
  const [me, setMe] = useState(null);

  const [cameras, setCameras] = useState([]);
  const [scopeInfo, setScopeInfo] = useState(null);
  const [loadingCameras, setLoadingCameras] = useState(true);
  const [cameraMsg, setCameraMsg] = useState("");

  const [loadingQuery, setLoadingQuery] = useState(false);
  const [queryMsg, setQueryMsg] = useState("");

  const [segments, setSegments] = useState([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);

  const today = useMemo(() => getTodayDateString(), []);

  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [selectedDate, setSelectedDate] = useState(today);
  const [visibleCalendarMonth, setVisibleCalendarMonth] = useState(
    toYearMonth(today)
  );
  const [recordedDates, setRecordedDates] = useState([]);
  const [recordedDatesMsg, setRecordedDatesMsg] = useState("");

  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("00:00");

  const [zoomLevel, setZoomLevel] = useState("hour");
  const [windowStartSec, setWindowStartSec] = useState(16 * 3600 + 30 * 60);
  const [playheadSec, setPlayheadSec] = useState(null);
  // 선택된 조각을 "이 초부터" 재생하라는 시작 오프셋(초). 타임라인 클릭 시크에 사용.
  const [playStartOffsetSec, setPlayStartOffsetSec] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  const timelineRef = useRef(null);
  const videoRef = useRef(null);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const selectedCamera = cameras.find(
    (cam) => String(cam.id) === String(selectedCameraId)
  );

  const selectedSegment = segments.find(
    (seg) => String(seg.id) === String(selectedSegmentId)
  );

  const visibleWindowSeconds = getZoomSeconds(zoomLevel);
  const windowEndSec = Math.min(windowStartSec + visibleWindowSeconds, 86400);

  useEffect(() => {
    const loadCameras = async () => {
      setLoadingCameras(true);
      setCameraMsg("");

      try {
        try {
          const meRes = await fetch("/api/me", { credentials: "include" });

          if (meRes.ok) {
            const meData = await meRes.json();
            const currentUser = meData?.user || meData?.me || meData || null;
            setMe(currentUser);
          }
        } catch (err) {
          console.warn("⚠️ /api/me 호출 실패", err);
        }

        const res = await fetch("/api/playback/my-scope-cameras", {
          credentials: "include",
        });

        const data = await res.json();

        if (!res.ok || !data?.success || !Array.isArray(data?.cameras)) {
          setCameraMsg(data?.message || "카메라 목록을 불러오지 못했습니다.");
          setCameras([]);
          return;
        }

        setScopeInfo({
          scope: data.scope || "",
          roleCode: data.roleCode || "",
        });

        const filtered = data.cameras.map((cam) =>
          normalizePlaybackCamera(cam)
        );

        setCameras(filtered);

        if (filtered.length === 0) {
          setSelectedCameraId("");
          setCameraMsg("조회 가능한 카메라가 없습니다.");
          return;
        }

        const targetCam =
          filtered.find((cam) => cam.streamKey === "NUXE3R5MZ8J7") ||
          filtered[0];

        setSelectedCameraId(String(targetCam.id));
      } catch (err) {
        console.error("❌ loadCameras error:", err);
        setCameraMsg("카메라 목록 조회 중 오류가 발생했습니다.");
        setCameras([]);
      } finally {
        setLoadingCameras(false);
      }
    };

    loadCameras();
  }, []);

  useEffect(() => {
    setVisibleCalendarMonth(toYearMonth(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    const loadRecordedDates = async () => {
      if (!selectedCameraId || !visibleCalendarMonth) {
        setRecordedDates([]);
        setRecordedDatesMsg("");
        return;
      }

      try {
        setRecordedDatesMsg("");

        const res = await fetch(
          `${PLAYBACK_API}/api/playback/recorded-dates?cameraId=${encodeURIComponent(
            selectedCameraId
          )}&yearMonth=${encodeURIComponent(visibleCalendarMonth)}`,
          { credentials: "include" }
        );

        const data = await res.json();

        if (!res.ok || !data?.success) {
          setRecordedDates([]);
          setRecordedDatesMsg(data?.error || "녹화 날짜 조회 실패");
          return;
        }

        setRecordedDates(Array.isArray(data?.dates) ? data.dates : []);
      } catch (err) {
        console.error("❌ loadRecordedDates error:", err);
        setRecordedDates([]);
        setRecordedDatesMsg("녹화 날짜 조회 중 오류");
      }
    };

    loadRecordedDates();
  }, [selectedCameraId, visibleCalendarMonth]);

  useEffect(() => {
    if (!videoRef.current) return;

    videoRef.current.playbackRate = playbackRate;
  }, [playbackRate, selectedSegmentId]);

  const handleSearch = async () => {
    if (!selectedCameraId) {
      alert("카메라를 선택해주세요.");
      return;
    }

    setLoadingQuery(true);
    setQueryMsg("");
    setSegments([]);
    setSelectedSegmentId(null);

    try {
      const url =
        `${PLAYBACK_API}/api/playback/query` +
        `?cameraId=${encodeURIComponent(selectedCameraId)}` +
        `&date=${encodeURIComponent(selectedDate)}` +
        `&from=${encodeURIComponent(`${startTime}:00`)}` +
        `&to=${encodeURIComponent(`${endTime}:00`)}`;

      const res = await fetch(url, {
        credentials: "include",
      });

      const data = await res.json();

      if (!res.ok || !data?.success) {
        setQueryMsg(data?.error || "조회 중 오류가 발생했습니다.");
        return;
      }

      const raw = Array.isArray(data?.segments) ? data.segments : [];
      const list = raw.map((seg, idx) => normalizePlaybackSegment(seg, idx));

      setSegments(list);

      const fromSec = parseTimeToSec(`${startTime}:00`);

      if (list.length > 0) {
        const firstSeg = list[0];
        const firstSegStartSec = getSegStartSec(firstSeg);

        const currentWindowSeconds = getZoomSeconds(zoomLevel);
        const nextWindowStart = clampWindowStart(
          firstSegStartSec - currentWindowSeconds / 2,
          currentWindowSeconds
        );

        setSelectedSegmentId(firstSeg.id);
        setPlayheadSec(firstSegStartSec);
        setPlayStartOffsetSec(0);
        setWindowStartSec(nextWindowStart);

        setQueryMsg(
          `${selectedCamera?.camName || "선택한 카메라"} / ${
            list.length
          }개의 녹화 구간 조회 완료`
        );
      } else {
        setWindowStartSec(fromSec);
        setPlayheadSec(fromSec);

        setQueryMsg(
          `${
            selectedCamera?.camName || "선택한 카메라"
          }에 해당 시간대 녹화 구간이 없습니다.`
        );
      }
    } catch (err) {
      console.error("❌ handleSearch error:", err);
      setQueryMsg("조회 중 오류가 발생했습니다.");
    } finally {
      setLoadingQuery(false);
    }
  };

  const handleDownload = () => {
    if (!selectedCameraId) {
      alert("카메라를 선택해주세요.");
      return;
    }

    // 기본 = 조회한 시간대(startTime~endTime) 전체.
    let from = `${startTime}:00`;
    let to = `${endTime}:00`;

    // 타임라인에서 특정 조각을 선택한 상태면 → 그 조각이 속한 "연속 구간"만 다운로드(C 방식).
    if (selectedSegment && segments.length) {
      const sorted = [...segments].sort(
        (a, b) => getSegStartSec(a) - getSegStartSec(b)
      );
      const i = sorted.findIndex(
        (s) => String(s.id) === String(selectedSegment.id)
      );

      if (i >= 0) {
        const TOL = 12; // 초: 이 이하 간격이면 '연속'으로 간주
        let lo = i;
        let hi = i;
        while (
          lo > 0 &&
          getSegStartSec(sorted[lo]) - getSegEndSec(sorted[lo - 1]) <= TOL
        )
          lo -= 1;
        while (
          hi < sorted.length - 1 &&
          getSegStartSec(sorted[hi + 1]) - getSegEndSec(sorted[hi]) <= TOL
        )
          hi += 1;

        const fmt = (sec) => {
          const s = Math.max(0, Math.floor(sec));
          const p = (n) => String(n).padStart(2, "0");
          return `${p(Math.floor(s / 3600))}:${p(
            Math.floor((s % 3600) / 60)
          )}:${p(s % 60)}`;
        };
        from = fmt(getSegStartSec(sorted[lo]) - 2);
        to = fmt(getSegEndSec(sorted[hi]) + 2);
      }
    }

    // 서버가 해당 범위의 조각들을 이어붙여 내려줌(끊긴 데는 나눠서 zip).
    const url =
      `${PLAYBACK_API}/api/playback/merge` +
      `?cameraId=${encodeURIComponent(selectedCameraId)}` +
      `&date=${encodeURIComponent(selectedDate)}` +
      `&from=${encodeURIComponent(from)}` +
      `&to=${encodeURIComponent(to)}`;

    window.open(url, "_blank");
  };

  // 조각 선택을 바꾸면서 "그 조각의 처음부터" 재생하도록 오프셋/플레이헤드도 함께 맞춤.
  const selectSegmentFromStart = (seg) => {
    if (!seg) return;
    setSelectedSegmentId(seg.id);
    setPlayStartOffsetSec(0);
    setPlayheadSec(getSegStartSec(seg));
  };

  const handleFirst = () => {
    if (segments.length === 0) return;
    selectSegmentFromStart(segments[0]);
  };

  const handlePrev = () => {
    if (!selectedSegment || segments.length === 0) return;

    const idx = segments.findIndex(
      (s) => String(s.id) === String(selectedSegment.id)
    );

    if (idx > 0) {
      selectSegmentFromStart(segments[idx - 1]);
    }
  };

  const handleNext = () => {
    if (!selectedSegment || segments.length === 0) return;

    const idx = segments.findIndex(
      (s) => String(s.id) === String(selectedSegment.id)
    );

    if (idx >= 0 && idx < segments.length - 1) {
      selectSegmentFromStart(segments[idx + 1]);
    }
  };

  const handleLast = () => {
    if (segments.length === 0) return;
    selectSegmentFromStart(segments[segments.length - 1]);
  };

  const jumpWindow = (direction) => {
    const unit = visibleWindowSeconds;
    const next = clampWindowStart(windowStartSec + direction * unit, unit);

    setWindowStartSec(next);
  };

  const handleZoomChange = (nextZoom) => {
    const nextWindow = getZoomSeconds(nextZoom);
    const anchor = playheadSec ?? parseTimeToSec(`${startTime}:00`);

    const nextStart = clampWindowStart(anchor - nextWindow / 2, nextWindow);

    setZoomLevel(nextZoom);
    setWindowStartSec(nextStart);
  };

  // NVR 방식: 타임라인에서 특정 시각을 클릭하면 그 시각이 포함된 녹화 파일을 골라
  //   "그 시각부터" 재생한다. 클릭 지점에 녹화가 없으면 가장 가까운 파일의 처음부터.
  const handleTimelineClick = (e) => {
    if (!timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));

    const clickedSec = windowStartSec + ratio * visibleWindowSeconds;
    const safeSec = Math.max(0, Math.min(86399, Math.floor(clickedSec)));

    setPlayheadSec(safeSec);

    // ── 이미 불러온 영상 안을 눌렀으면 <새로 받지 않고> 그 지점으로 옮긴다 ──────
    //   예전에는 아래(15초 조각 번호)로 비교했다. 그런데 지금 불러오는 영상은 1시간짜리라
    //   그 안에 조각이 240개나 있어서, 같은 영상 안을 눌러도 매번 "다른 조각"이 되어
    //   영상을 새로 받고 재생 위치가 00:00 으로 되돌아갔다.
    //   그래서 <아래 타임라인 클릭>과 <플레이어 막대 끌기>가 같은 지점을 가리키는데도
    //   동작이 달라 헷갈렸다(2026-09-30 지적).
    //   되감기(부분 요청)가 되게 만든 뒤로는 새로 받을 이유가 없다 — 그냥 옮기면 된다.
    //
    //   길이는 <실제로 불러온 영상>에서 읽는다. 서버가 요청한 만큼 다 주지 않을 수 있어
    //   계산값을 믿으면 어긋난다(오늘 그것 때문에 한 번 깨졌다).
    const loadedVideo = videoRef.current;
    const anchorStartSec = selectedSegment ? getSegStartSec(selectedSegment) : null;
    const loadedLenSec =
      loadedVideo && Number.isFinite(loadedVideo.duration) ? loadedVideo.duration : 0;

    if (
      anchorStartSec != null &&
      loadedLenSec > 0 &&
      safeSec >= anchorStartSec &&
      safeSec < anchorStartSec + loadedLenSec
    ) {
      const offsetInLoaded = safeSec - anchorStartSec;
      try {
        loadedVideo.currentTime = offsetInLoaded;
        const p = loadedVideo.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch (err) {}
      // 나중에 다시 불러오는 일이 생겨도 같은 지점에서 이어지도록 오프셋을 맞춰 둔다.
      // (이 값은 영상 주소에 들어가지 않으므로 다시 받지 않는다)
      setPlayStartOffsetSec(offsetInLoaded);
      return;
    }

    const containing = findSegmentContainingSec(segments, safeSec);

    if (containing) {
      // 그 시각이 포함된 파일 → 파일 내 오프셋(초)만큼 건너뛰어 재생
      const offset = Math.max(0, safeSec - getSegStartSec(containing));

      if (String(containing.id) === String(selectedSegmentId)) {
        // 이미 재생 중인 파일 안을 클릭 → 즉시 그 지점으로 시크
        const v = videoRef.current;
        if (v) {
          try {
            v.currentTime = offset;
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
          } catch (err) {}
        }
        setPlayStartOffsetSec(offset);
      } else {
        setPlayStartOffsetSec(offset);
        setSelectedSegmentId(containing.id);
      }
      return;
    }

    // 클릭 지점에 녹화 없음 → 가장 가까운 파일의 처음부터
    const closest = findClosestSegmentBySec(segments, safeSec);
    if (closest) {
      setPlayStartOffsetSec(0);
      setSelectedSegmentId(closest.id);
    }
  };

  const timelineRuns = useMemo(() => {
    return buildTimelineRuns({
      segments,
      windowStartSec,
      windowEndSec,
      visibleWindowSeconds,
    });
  }, [segments, windowStartSec, windowEndSec, visibleWindowSeconds]);

  const playheadPercent =
    playheadSec == null
      ? null
      : ((playheadSec - windowStartSec) / visibleWindowSeconds) * 100;

  const axisLabels = useMemo(() => {
    return buildAxisLabels({
      isMobile,
      windowStartSec,
      visibleWindowSeconds,
    });
  }, [windowStartSec, visibleWindowSeconds, isMobile]);

  return (
    <div className="min-h-0 overflow-visible pb-[calc(96px+env(safe-area-inset-bottom))] lg:h-full lg:overflow-hidden lg:pb-0">
      <div className="flex min-h-0 flex-col gap-3 overflow-visible lg:h-full lg:overflow-hidden">
        <PlaybackSearchPanel
          cameras={cameras}
          loadingCameras={loadingCameras}
          selectedCameraId={selectedCameraId}
          onSelectedCameraIdChange={setSelectedCameraId}
          selectedDate={selectedDate}
          onSelectedDateChange={setSelectedDate}
          recordedDates={recordedDates}
          visibleCalendarMonth={visibleCalendarMonth}
          onVisibleCalendarMonthChange={setVisibleCalendarMonth}
          startTime={startTime}
          onStartTimeChange={setStartTime}
          endTime={endTime}
          onEndTimeChange={setEndTime}
          loadingQuery={loadingQuery}
          onSearch={handleSearch}
          cameraMsg={cameraMsg}
        />

        {(queryMsg || recordedDatesMsg) && (
          <div className="shrink-0 rounded-2xl bg-white px-4 py-3 text-sm shadow-md dark:bg-[#1F2A3B]">
            {queryMsg && (
              <div className="break-words text-gray-700 dark:text-gray-200">
                {queryMsg}
              </div>
            )}

            {recordedDatesMsg && (
              <div className="break-words text-red-500">{recordedDatesMsg}</div>
            )}
          </div>
        )}

        <div className="grid min-h-0 flex-1 grid-cols-12 gap-3 overflow-visible lg:overflow-hidden">
          <div className="col-span-12 min-w-0 lg:col-span-2 lg:min-h-0 lg:overflow-hidden">
            <PlaybackCameraList
              cameras={cameras}
              loadingCameras={loadingCameras}
              selectedCameraId={selectedCameraId}
              onSelectedCameraIdChange={setSelectedCameraId}
            />
          </div>

          <div className="col-span-12 flex min-h-0 min-w-0 flex-col gap-3 overflow-visible lg:col-span-10 lg:overflow-hidden">
            <PlaybackVideoPanel
              selectedCamera={selectedCamera}
              selectedDate={selectedDate}
              startTime={startTime}
              endTime={endTime}
              selectedSegment={selectedSegment}
              videoRef={videoRef}
              playbackRate={playbackRate}
              onPlaybackRateChange={setPlaybackRate}
              onFirst={handleFirst}
              onPrev={handlePrev}
              onNext={handleNext}
              onLast={handleLast}
              onDownload={handleDownload}
              playheadSec={playheadSec}
              playStartOffsetSec={playStartOffsetSec}
              onPlayheadUpdate={setPlayheadSec}
            />

            <PlaybackTimeline
              selectedDate={selectedDate}
              zoomLevel={zoomLevel}
              onZoomChange={handleZoomChange}
              onJumpWindow={jumpWindow}
              windowStartSec={windowStartSec}
              windowEndSec={windowEndSec}
              axisLabels={axisLabels}
              timelineRef={timelineRef}
              onTimelineClick={handleTimelineClick}
              timelineRuns={timelineRuns}
              playheadSec={playheadSec}
              playheadPercent={playheadPercent}
            />
          </div>
        </div>
      </div>
    </div>
  );
}